import { useCallback } from 'react'
import { useSearchParams } from 'react-router'
import { Cargando } from '../componentes/Cargando'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { IconoConversaciones } from '../componentes/iconos'
import { Conversacion } from './conversaciones/Conversacion'
import { ListaSesiones } from './conversaciones/ListaSesiones'
import { TAMANO_PAGINA, useSesiones, type Canal } from './conversaciones/consultas'

export type Seccion = 'clientes' | 'leads'

/**
 * Las dos delegaciones de leads son **dos tablas distintas**, no un filtro.
 * Viven en la misma pantalla con un selector, y la eleccion va en la url y no
 * en un estado interno: asi el enlace a "los leads de Valencia" se puede pegar
 * en un chat, igual que los filtros de Logistica.
 */
const DELEGACIONES = [
  { clave: 'castellon', etiqueta: 'Castellon', canal: 'leads' },
  { clave: 'valencia', etiqueta: 'Valencia', canal: 'leads_valencia' },
] as const

type ClaveDelegacion = (typeof DELEGACIONES)[number]['clave']

const TEXTOS: Record<Seccion, { titulo: string; descripcion: string }> = {
  clientes: {
    titulo: 'Conversaciones',
    descripcion: 'Lo que los clientes han hablado con el asistente de WhatsApp.',
  },
  leads: {
    titulo: 'Leads',
    descripcion: 'Contactos nuevos por delegacion, con los datos que se les fueron pidiendo.',
  },
}

function leerPagina(valor: string | null): number {
  const numero = Number.parseInt(valor ?? '', 10)
  return Number.isFinite(numero) && numero >= 1 ? numero : 1
}

function leerDelegacion(valor: string | null): ClaveDelegacion {
  return DELEGACIONES.some((opcion) => opcion.clave === valor)
    ? (valor as ClaveDelegacion)
    : 'castellon'
}

interface Props {
  /** Viene de la ruta (`/conversaciones/clientes` o `/conversaciones/leads`). */
  seccion: Seccion
}

export function Conversaciones({ seccion }: Props) {
  const [parametros, setParametros] = useSearchParams()

  const delegacion = leerDelegacion(parametros.get('delegacion'))
  const canal: Canal =
    seccion === 'clientes'
      ? 'clientes'
      : (DELEGACIONES.find((opcion) => opcion.clave === delegacion)?.canal ?? 'leads')
  const sesionId = parametros.get('sesion')
  const pagina = leerPagina(parametros.get('page'))

  const sesiones = useSesiones(canal, pagina)
  const items = sesiones.data?.items ?? []
  const total = sesiones.data?.total ?? 0
  const tamano = sesiones.data?.page_size || TAMANO_PAGINA
  const paginas = Math.max(1, Math.ceil(total / tamano))
  const resumen = items.find((sesion) => sesion.id === sesionId) ?? null

  const actualizar = useCallback(
    (mutar: (siguientes: URLSearchParams) => void) => {
      const siguientes = new URLSearchParams(parametros)
      mutar(siguientes)
      // Sin `replace`: abrir una conversacion o cambiar de delegacion son
      // acciones deliberadas, y "atras" tiene que deshacerlas en vez de sacar
      // al usuario de la aplicacion.
      setParametros(siguientes)
    },
    [parametros, setParametros],
  )

  const abrir = useCallback(
    (id: string) => actualizar((siguientes) => siguientes.set('sesion', id)),
    [actualizar],
  )

  const volver = useCallback(
    () => actualizar((siguientes) => siguientes.delete('sesion')),
    [actualizar],
  )

  const cambiarDelegacion = useCallback(
    (clave: string) =>
      actualizar((siguientes) => {
        if (clave === 'castellon') siguientes.delete('delegacion')
        else siguientes.set('delegacion', clave)
        // Un identificador de Castellon no significa nada en Valencia, y la
        // pagina 2 de un listado no vale para el otro.
        siguientes.delete('sesion')
        siguientes.delete('page')
      }),
    [actualizar],
  )

  const irAPagina = useCallback(
    (numero: number) =>
      actualizar((siguientes) => {
        if (numero <= 1) siguientes.delete('page')
        else siguientes.set('page', String(numero))
        siguientes.delete('sesion')
      }),
    [actualizar],
  )

  const textos = TEXTOS[seccion]

  return (
    <div className="flex flex-col gap-4">
      {/* Con la conversacion abierta en movil, la cabecera cede la pantalla:
          esta seccion se consulta sobre todo desde el telefono. */}
      <header className={sesionId ? 'hidden lg:block' : 'block'}>
        <h1 className="text-xl font-semibold text-tinta">{textos.titulo}</h1>
        <p className="mt-1 text-sm text-tinta-apagada">{textos.descripcion}</p>
      </header>

      <div className="grid items-start gap-4 lg:grid-cols-[22rem_minmax(0,1fr)]">
        <section
          aria-label="Listado de conversaciones"
          className={`min-w-0 flex-col gap-3 ${sesionId ? 'hidden lg:flex' : 'flex'}`}
        >
          {seccion === 'leads' ? (
            <div>
              <label htmlFor="delegacion" className="block text-sm font-medium text-tinta-2">
                Delegacion
              </label>
              <select
                id="delegacion"
                value={delegacion}
                onChange={(evento) => cambiarDelegacion(evento.target.value)}
                className="mt-1 w-full rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo"
              >
                {DELEGACIONES.map((opcion) => (
                  <option key={opcion.clave} value={opcion.clave}>
                    {opcion.etiqueta}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {sesiones.isPending ? (
            <Cargando mensaje="Cargando conversaciones" />
          ) : sesiones.isError ? (
            <EstadoError error={sesiones.error} onReintentar={() => void sesiones.refetch()} />
          ) : items.length === 0 ? (
            <EstadoVacio
              titulo="No hay conversaciones en este canal"
              descripcion="El listado ha llegado vacio de la API."
            />
          ) : (
            <>
              <p className="text-xs text-tinta-apagada">
                {total} conversaciones{paginas > 1 ? `, pagina ${pagina} de ${paginas}` : ''}
              </p>

              <ListaSesiones sesiones={items} seleccionada={sesionId} onSeleccionar={abrir} />

              {paginas > 1 ? (
                <nav
                  aria-label="Paginacion de conversaciones"
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <button
                    type="button"
                    aria-label="Pagina anterior"
                    disabled={pagina <= 1}
                    onClick={() => irAPagina(pagina - 1)}
                    className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:cursor-not-allowed disabled:border-borde disabled:text-desactivado-tinta"
                  >
                    Anterior
                  </button>
                  <span className="text-tinta-apagada">
                    Pagina {pagina} de {paginas}
                  </span>
                  <button
                    type="button"
                    aria-label="Pagina siguiente"
                    disabled={pagina >= paginas}
                    onClick={() => irAPagina(pagina + 1)}
                    className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:cursor-not-allowed disabled:border-borde disabled:text-desactivado-tinta"
                  >
                    Siguiente
                  </button>
                </nav>
              ) : null}
            </>
          )}
        </section>

        {/* `min-w-0`: sin el, la columna de la rejilla se ensancha hasta el
            contenido mas largo que no se puede partir. Medido en el navegador a
            375 px con una conversacion real: un cliente mando un enlace de
            Google Maps de 180 caracteres y la pagina paso a medir 2.525 px de
            ancho, con barra horizontal. Con `min-w-0` la columna se queda en la
            pantalla y el enlace se parte dentro de su burbuja. */}
        <div className={`min-w-0 ${sesionId ? 'block' : 'hidden lg:block'}`}>
          {sesionId ? (
            // La clave reinicia el panel al cambiar de conversacion: sin ella,
            // los metadatos plegados o el scroll se quedarian de la anterior.
            <Conversacion
              key={`${canal}:${sesionId}`}
              canal={canal}
              sesionId={sesionId}
              resumen={resumen}
              onVolver={volver}
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-borde-fuerte bg-superficie px-6 py-12 text-center">
              <IconoConversaciones aria-hidden="true" className="size-8 text-tinta-apagada" />
              <p className="text-sm text-tinta-apagada">
                Selecciona una conversacion del listado para leerla.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
